import axios from "axios";
import { cookies } from "roboflex-thalamus-utils-lib";
import { upsertSavedAccount } from "roboflex-thalamus-request-handler";

const apiBase = process.env.VUE_APP_ROOT_API || process.env.VUE_APP_ROOT_DEV;

const SSO_COOKIE_NAME = "thalamusSession";
const COOKIE_DOMAIN = "thalamus.ind.br";
const COOKIE_PATH = "/";
const EXPIRACAO_TOKEN = 1;

const api = axios.create({
  baseURL: apiBase,
});

const MENSAGENS_ERRO = {
  user_not_found: "E-mail Google não cadastrado no Thalamus.",
  domain_not_allowed: "Use uma conta do Google Workspace da empresa.",
  user_inactive: "Usuário inativo. Entre em contato com o administrador.",
  oauth_failed: "Não foi possível concluir o login com Google.",
  invalid_code: "Sessão expirada. Tente entrar com Google novamente.",
  no_code: "Autorização Google cancelada ou incompleta.",
  invalid_state: "Sessão de login inválida. Tente novamente.",
  invalid_credential: "Credencial Google inválida ou expirada.",
  invalid_credentials: "E-mail e/ou senha estão incorretos.",
  empresa_required: "Informe a empresa para este e-mail.",
};

function mensagemErroGoogle(code) {
  return MENSAGENS_ERRO[code] || MENSAGENS_ERRO.oauth_failed;
}

function mensagemErroLogin(code, fallback) {
  return MENSAGENS_ERRO[code] || fallback || MENSAGENS_ERRO.invalid_credentials;
}

/** Em development/local, autentica na API local (não no SSO de produção). */
function usarLoginLocal() {
  const env = process.env.NODE_ENV;
  return env === "development" || env === "local";
}

/**
 * Domínio do cookie de sessão.
 * Em localhost/127.0.0.1 não pode ser thalamus.ind.br (o browser rejeita).
 */
export function domainCookieSessao() {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return "";
    }
  }
  return process.env.VUE_APP_ROOT_SSO_DOMAIN || COOKIE_DOMAIN;
}

/**
 * Monta o valor do cookie thalamusSession no formato esperado pelo SSO:
 * encodeURIComponent('object=' + JSON.stringify({ id, nome, token, tokenType, local, ... }))
 */
export function montarValorCookieSessao(usuario) {
  return encodeURIComponent("object=" + JSON.stringify(usuario));
}

/** Segundos até o `exp` do JWT. Null quando o token não tem claim válida. */
export function segundosRestantesToken(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    let payloadB64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = payloadB64.length % 4;
    if (pad) payloadB64 += "=".repeat(4 - pad);
    const exp = Number(JSON.parse(atob(payloadB64))?.exp);
    if (!Number.isFinite(exp)) return null;
    return exp - Math.floor(Date.now() / 1000);
  } catch {
    return null;
  }
}

/**
 * Grava thalamusSession com validade igual à do JWT.
 * Sem `exp`, mantém o fallback de 1 dia.
 */
export function gravarCookieSessao(usuario) {
  const valor = montarValorCookieSessao(usuario);
  const domain = domainCookieSessao();
  const segundos = segundosRestantesToken(usuario?.token);
  const maxAge = segundos == null ? EXPIRACAO_TOKEN * 24 * 60 * 60 : Math.floor(segundos);
  if (maxAge <= 0) return;

  try {
    cookies.setCookieComplete(
      SSO_COOKIE_NAME,
      valor,
      maxAge / (24 * 60 * 60),
      COOKIE_PATH,
      domain || undefined
    );
  } catch {
    // fallback manual
  }

  // Garante gravação em localhost (Domain=thalamus.ind.br seria rejeitado).
  if (typeof document !== "undefined") {
    let cookie = `${SSO_COOKIE_NAME}=${valor}; path=${COOKIE_PATH}; max-age=${maxAge}; SameSite=Lax`;
    if (domain) {
      cookie += `; domain=${domain}`;
    }
    document.cookie = cookie;
  }
}

/** Renova o JWT sem passar pelo axios compartilhado (que redireciona no 401). */
async function renovarToken(token) {
  const response = await api.post("/auth/refresh", null, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
}

function urlRedirectGoogle() {
  const base = (apiBase || "").replace(/\/$/, "");
  return `${base}/auth/google/redirect`;
}

async function trocarCodigoGoogle(code) {
  const response = await api.post("/auth/google/session", { code });
  return response.data;
}

async function loginOneTap(credential) {
  const response = await api.post("/auth/google/one-tap", { credential });
  return response.data;
}

/**
 * Login e-mail/senha na API local (JWT + claims empresa_id/is_root).
 */
async function loginLocal(email, password, empresaId = null) {
  const payload = { email, password };
  if (empresaId != null && empresaId !== "") {
    payload.empresa_id = Number(empresaId);
  }
  const response = await api.post("/auth/login", payload);
  return response.data;
}

/**
 * Converte payload de /auth/login (ou Google) no objeto de sessão e grava thalamusSession.
 * Formato alinhado à produção: { id, nome, token, tokenType, local }
 */
function registrarSessaoGoogle(payload) {
  const { user, access_token, token_type } = payload;
  const usuarioLogado = {
    id: user.id,
    nome: user.name,
    token: access_token,
    tokenType: token_type || "Bearer",
    local: null,
    empresa_id: user.empresa_id ?? null,
    is_root: !!user.is_root,
  };
  gravarCookieSessao(usuarioLogado);
  try {
    upsertSavedAccount(usuarioLogado);
  } catch {
    // limíte do cofre ou lib antiga; sessionService reforça após /auth/me
  }
  return usuarioLogado;
}

export default {
  urlRedirectGoogle,
  trocarCodigoGoogle,
  loginOneTap,
  loginLocal,
  usarLoginLocal,
  registrarSessaoGoogle,
  renovarToken,
  montarValorCookieSessao,
  domainCookieSessao,
  mensagemErroGoogle,
  mensagemErroLogin,
};
